import React, { useEffect, useMemo, useState } from 'react';
import { useCapabilities } from '../../hooks/useCapabilities';
import {
  QUICK_PRESETS,
  QUICK_PRESET_LABELS,
  QuickPreset,
  classifyAction,
  prettyResource,
  summarizeScopes,
} from '../../utility/scopes';
import '../../styles/ApiKeyScopePicker.scss';

interface Props {
  /** Fires whenever the selection changes. `valid` is false when advanced mode has nothing picked. */
  onChange: (scopes: string[], valid: boolean) => void;
  /** Defaults to read-only so a new key cannot change or remove resources by accident. */
  defaultPreset?: QuickPreset;
}

type Mode = 'quick' | 'advanced';

const QUICK_ORDER: QuickPreset[] = ['read', 'read_write', 'read_write_delete', 'full'];

/**
 * API-key scope selector. Quick presets (default Read-only) cover the common cases;
 * Advanced exposes the live resource x action catalog for least-privilege keys.
 * Controlled via onChange only - mount it with a fresh key to reset.
 */
const ApiKeyScopePicker: React.FC<Props> = ({ onChange, defaultPreset = 'read' }) => {
  const { resources, loading, error } = useCapabilities();
  const [mode, setMode] = useState<Mode>('quick');
  const [quick, setQuick] = useState<QuickPreset>(defaultPreset);
  // Advanced selection as a flat token set: "<resource>:*" or "<resource>:<action>".
  const [tokens, setTokens] = useState<Set<string>>(new Set());

  const scopes = useMemo(
    () => (mode === 'quick' ? QUICK_PRESETS[quick] : Array.from(tokens)),
    [mode, quick, tokens]
  );
  const valid = scopes.length > 0;

  useEffect(() => {
    onChange(scopes, valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopes, valid]);

  const allChecked = (resource: string) => tokens.has(`${resource}:*`);
  const actionChecked = (resource: string, action: string) =>
    tokens.has(`${resource}:*`) || tokens.has(`${resource}:${action}`);

  const toggleAll = (resource: string) => {
    setTokens((prev) => {
      const next = new Set(prev);
      const star = `${resource}:*`;
      if (next.has(star)) {
        next.delete(star);
      } else {
        Array.from(next).forEach((t) => {
          if (t.startsWith(`${resource}:`)) next.delete(t);
        });
        next.add(star);
      }
      return next;
    });
  };

  const toggleAction = (resource: string, action: string) => {
    setTokens((prev) => {
      const next = new Set(prev);
      const token = `${resource}:${action}`;
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return next;
    });
  };

  return (
    <div className="scope-picker">
      <div className="scope-tabs" role="tablist">
        <button
          type="button"
          className={mode === 'quick' ? 'active' : ''}
          onClick={() => setMode('quick')}
        >
          Quick
        </button>
        <button
          type="button"
          className={mode === 'advanced' ? 'active' : ''}
          onClick={() => setMode('advanced')}
        >
          Advanced
        </button>
      </div>

      {mode === 'quick' ? (
        <div className="scope-quick">
          {QUICK_ORDER.map((preset) => (
            <label key={preset} className={`quick-option ${quick === preset ? 'on' : ''}`}>
              <input
                type="radio"
                name="quick-scope"
                checked={quick === preset}
                onChange={() => setQuick(preset)}
              />
              <span>{QUICK_PRESET_LABELS[preset]}</span>
            </label>
          ))}
          {quick === 'full' && (
            <p className="scope-risk-note" role="status">
              Full access can create, change, and delete resources. Use it only when the client genuinely needs it. It does not
              include the data inside your databases: grant that under managed postgres data.
            </p>
          )}
        </div>
      ) : (
        <div className="scope-advanced">
          {loading && <div className="scope-hint">Loading capabilities...</div>}
          {error && <div className="scope-hint error">{error}</div>}
          {!loading &&
            !error &&
            resources.map((r) => (
              <div className="scope-row" key={r.type}>
                <label className="scope-res">
                  <input
                    type="checkbox"
                    checked={allChecked(r.type)}
                    onChange={() => toggleAll(r.type)}
                  />
                  <span className="res-name">{prettyResource(r.type)}</span>
                  <span className="all-tag">all</span>
                </label>
                <div className="scope-actions">
                  {r.actions.map((a) => (
                    <label
                      key={a}
                      className={`action-chip verb-${classifyAction(a)} ${
                        actionChecked(r.type, a) ? 'on' : ''
                      } ${allChecked(r.type) ? 'locked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={actionChecked(r.type, a)}
                        disabled={allChecked(r.type)}
                        onChange={() => toggleAction(r.type, a)}
                      />
                      {a}
                    </label>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      <div className={`scope-summary ${valid ? '' : 'invalid'}`}>{summarizeScopes(scopes)}</div>
    </div>
  );
};

export default ApiKeyScopePicker;
